import React from 'react';
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
  useSearchParams
} from 'react-router-dom';
import App from './App';
import LoginPage from './components/auth/LoginPage';
import LandingPage from './pages/public/LandingPage';
import DemoPage from './pages/public/DemoPage';
import PrivacyPage from './pages/public/PrivacyPage';
import SecurityPage from './pages/public/SecurityPage';
import TermsPage from './pages/public/TermsPage';
import SupportPage from './pages/public/SupportPage';
import StatusPage from './pages/public/StatusPage';
import ForgotPasswordPage from './pages/public/ForgotPasswordPage';
import ResetPasswordPage from './pages/public/ResetPasswordPage';
import TimeClockPage from './pages/TimeClockPage';
import KioskPage from './pages/KioskPage';
import MobilePunchPage from './pages/MobilePunchPage';
import { useAuth } from './context/AuthContext';

/**
 * Top-level router introduced in W0 (Packaging Sprint).
 *
 * Splits the surface into:
 *   - Public routes: /, /login, /demo, and the five trust pages
 *   - Everything else: the existing authenticated App shell
 *
 * App.js keeps owning its own internal navigation (pushState + popstate).
 * Both the source and target of in-app navigations fall under the catch-all
 * `*` route, so React Router never unmounts App during internal transitions.
 */

const DEFAULT_AUTHED_PATH = '/my-dashboard';

function getDefaultAuthedPath(user) {
  if (user?.role === 'employee' || user?.role === 'supervisor') {
    return '/time-clock';
  }
  if (user?.role === 'super_admin' || user?.role === 'platform_admin') {
    return '/admin-workspace';
  }
  if (user?.role === 'director' || user?.role === 'admin') {
    return '/executive';
  }
  if (user?.role === 'airport_manager') {
    return '/station-dashboard';
  }
  return DEFAULT_AUTHED_PATH;
}

function LoadingScreen() {
  return (
    <div className="app-loading-screen">
      <div className="app-loading-card">
        <h1>Loading secure dashboard</h1>
        <p>Checking your session before any KPI data is shown.</p>
      </div>
    </div>
  );
}

/**
 * Gate for everything that expects a signed-in user. Preserves deep links
 * via `?next=` so the post-login redirect returns the visitor exactly where
 * they tried to go.
 */
function RequireAuth({ children }) {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) return <LoadingScreen />;
  if (!isAuthenticated) {
    const nextPath = `${location.pathname}${location.search}${location.hash}`;
    const suffix = nextPath && nextPath !== '/' ? `?next=${encodeURIComponent(nextPath)}` : '';
    return <Navigate to={`/login${suffix}`} replace />;
  }
  return children;
}

/**
 * Guard for public routes that should bounce already-signed-in users back
 * into the app (currently just /login). Honors `?next=` if present.
 */
function RedirectIfAuthed({ children, fallback = DEFAULT_AUTHED_PATH }) {
  const { isAuthenticated, isLoading, user } = useAuth();
  const [params] = useSearchParams();

  if (isLoading) return <LoadingScreen />;
  if (isAuthenticated) {
    const rawNext = params.get('next');
    const target = isSafeInternalPath(rawNext) ? rawNext : getDefaultAuthedPath(user) || fallback;
    return <Navigate to={target} replace />;
  }
  return children;
}

/**
 * Defense-in-depth against open-redirect: only accept relative app paths
 * from the query string.
 */
function isSafeInternalPath(value) {
  if (!value || typeof value !== 'string') return false;
  if (!value.startsWith('/')) return false;
  if (value.startsWith('//')) return false; // protocol-relative
  return true;
}

function LoginRoute() {
  const { login, authError } = useAuth();
  return (
    <LoginPage
      errorMessage=""
      onLogin={login}
      systemError={authError}
    />
  );
}

export default function AppRouter() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<RedirectIfAuthed><LoginRoute /></RedirectIfAuthed>} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/" element={<RedirectIfAuthed><LandingPage /></RedirectIfAuthed>} />
        <Route path="/demo" element={<DemoPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/security" element={<SecurityPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/support" element={<SupportPage />} />
        <Route path="/status" element={<StatusPage />} />
        <Route path="/time-clock" element={<RequireAuth><TimeClockPage /></RequireAuth>} />
        <Route path="/kiosk" element={<KioskPage />} />
        <Route path="/mobile-punch/:token" element={<MobilePunchPage />} />
        <Route path="*" element={<RequireAuth><App /></RequireAuth>} />
      </Routes>
    </BrowserRouter>
  );
}

export { getDefaultAuthedPath };
